import { Link } from '@tanstack/react-router'
import { CheckCircle2, ChevronDown, Home, LogOut, Menu } from 'lucide-react'
import { useRef, useState } from 'react'
import { useNavigationBar } from '../hooks/useNavigationBar'
import { roles } from '@/features/users/constants/roles'
import { UserRoleEnum } from '@/features/users/types/user'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import { VisuallyHidden } from '@/components/ui/visually-hidden'

export default function MobileNavigation() {
  const {
    navigate,
    user,
    activeRole,
    logout,
    handleRoleChange,
    getVisibleDonationsItems,
    getVisibleInventoryItems,
    getVisibleExpensesItems,
    getVisibleAdminItems,
    getVisibleSettingsItems,
    isPathActive,
  } = useNavigationBar()

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [showRoleOptions, setShowRoleOptions] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const handleDrawerOpenChange = (open: boolean) => {
    setDrawerOpen(open)
    if (!open) {
      setShowRoleOptions(false)
    }
    if (!open && document.activeElement !== triggerRef.current) {
      setTimeout(() => {
        triggerRef.current?.focus()
      }, 100)
    }
  }

  const getRoleDisplayName = (roleId?: string | null) => {
    const roleName = roles.find((r) => r._id === roleId)?.name || 'Unknown Role'
    const isPastor = roleId === UserRoleEnum.PASTOR.toString()

    return isPastor && user?.isAdminPastor ? `${roleName} (Admin)` : roleName
  }

  const activeRoleDisplay = getRoleDisplayName(activeRole)

  const userRoles =
    user?.roles
      .map((roleId) => roles.find((r) => r._id === roleId))
      .filter(Boolean) || []

  const visibleDonationsItems = getVisibleDonationsItems()
  const visibleInventoryItems = getVisibleInventoryItems()
  const visibleExpensesItems = getVisibleExpensesItems()
  const visibleAdminItems = getVisibleAdminItems()
  const visibleSettingsItems = getVisibleSettingsItems()

  // Check if user is inventory-only
  const isInventoryOnly =
    user?.roles.length === 1 &&
    user.roles[0] === UserRoleEnum.INVENTORY.toString()

  // Main navigation items for bottom nav
  const mainNavItems = isInventoryOnly
    ? [
        visibleInventoryItems.find((item) => item.to === '/inventory'),
        visibleInventoryItems.find(
          (item) => item.to === '/inventory/transactions',
        ),
        visibleInventoryItems.find(
          (item) => item.to === '/inventory/categories',
        ),
      ].filter((item) => !!item)
    : [
        { to: '/', label: 'Home', icon: <Home size={20} /> },
        visibleDonationsItems.length > 0 && visibleDonationsItems[0],
        visibleAdminItems.find((item) => item.to === '/members'),
      ].filter((item) => !!item)

  const handleNavigation = (to: string) => {
    navigate({ to })
    handleDrawerOpenChange(false)
  }

  return (
    <div className="lg:hidden">
      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 z-50" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
        <div className="flex items-center justify-around max-w-lg mx-auto">
          {/* Main Navigation Items */}
          {mainNavItems.map((item) => {
            const active = isPathActive(item.to)
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex flex-col items-center gap-1 px-3 py-3 rounded-md transition-colors duration-200 min-w-0 ${
                  active
                    ? 'bg-neutral-100 font-semibold'
                    : 'hover:bg-neutral-100'
                }`}
              >
                <span>{item.icon}</span>
                <span className="text-xs font-medium truncate">
                  {item.label}
                </span>
              </Link>
            )
          })}

          {/* Menu Button */}
          <Drawer
            open={drawerOpen}
            onOpenChange={handleDrawerOpenChange}
            shouldScaleBackground={false}
          >
            <DrawerTrigger asChild>
              <button
                ref={triggerRef}
                className="flex flex-col items-center gap-1 px-3 py-3 rounded-md hover:bg-neutral-100 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-200"
              >
                <Menu size={20} />
                <span className="text-xs font-medium">More</span>
              </button>
            </DrawerTrigger>
            <DrawerContent className="min-h-[95vh] flex flex-col overflow-hidden">
              <VisuallyHidden>
                <DrawerTitle>Navigation Menu</DrawerTitle>
                <DrawerDescription>
                  Access additional navigation options, settings, and account
                  management
                </DrawerDescription>
              </VisuallyHidden>

              {/* User Info */}
              <div className="shrink-0 mx-4 mt-4 mb-4 p-4 bg-neutral-50 rounded-md">
                <div className="font-medium text-base">
                  {user?.name ?? 'User'}
                </div>
                {userRoles.length > 1 ? (
                  <div className="mt-3 rounded-md border border-neutral-200 bg-white">
                    <button
                      type="button"
                      onClick={() => setShowRoleOptions((prev) => !prev)}
                      aria-expanded={showRoleOptions}
                      className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-neutral-50 transition-colors duration-200"
                    >
                      <span className="text-sm font-medium text-neutral-800">
                        {activeRoleDisplay}
                      </span>
                      <ChevronDown
                        size={14}
                        className={`text-neutral-500 transition-transform duration-200 ${
                          showRoleOptions ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {showRoleOptions ? (
                      <div className="border-t border-neutral-200 p-2 space-y-1">
                        {userRoles.map((roleOption) => {
                          if (!roleOption) return null

                          const isActive = activeRole === roleOption._id

                          return (
                            <button
                              key={roleOption._id}
                              type="button"
                              onClick={() => {
                                handleRoleChange(roleOption._id)
                                setShowRoleOptions(false)
                                handleDrawerOpenChange(false)
                              }}
                              className={`w-full flex items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors duration-200 ${
                                isActive
                                  ? 'bg-neutral-100 text-neutral-900'
                                  : 'text-neutral-700 hover:bg-neutral-50'
                              }`}
                            >
                              <CheckCircle2
                                size={15}
                                className={
                                  isActive
                                    ? 'text-neutral-800'
                                    : 'text-neutral-300'
                                }
                              />
                              <span className={isActive ? 'font-medium' : ''}>
                                {getRoleDisplayName(roleOption._id)}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="text-neutral-500 text-sm">
                    {activeRoleDisplay}
                  </div>
                )}
              </div>

              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto px-4">
                <div className="pb-6">
                  {/* Donations Group */}
                  {visibleDonationsItems.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-neutral-500 mb-3 px-4">
                        Donations
                      </h3>
                      <div className="space-y-1">
                        {visibleDonationsItems.map((item) => {
                          const active = isPathActive(item.to)
                          return (
                            <button
                              key={item.to}
                              onClick={() => handleNavigation(item.to)}
                              className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-md transition-colors duration-200 ${
                                active
                                  ? 'bg-neutral-100 font-semibold'
                                  : 'hover:bg-neutral-100'
                              }`}
                            >
                              <span>{item.icon}</span>
                              <span>{item.label}</span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Inventory Group */}
                  {visibleInventoryItems.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-neutral-500 mb-3 px-4">
                        Inventory
                      </h3>
                      <div className="space-y-1">
                        {visibleInventoryItems.map((item) => {
                          const active = isPathActive(item.to)
                          return (
                            <button
                              key={item.to}
                              onClick={() => handleNavigation(item.to)}
                              className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-md transition-colors duration-200 ${
                                active
                                  ? 'bg-neutral-100 font-semibold'
                                  : 'hover:bg-neutral-100'
                              }`}
                            >
                              <span>{item.icon}</span>
                              <span>{item.label}</span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Expenses Group */}
                  {visibleExpensesItems.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-neutral-500 mb-3 px-4">
                        Expenses
                      </h3>
                      <div className="space-y-1">
                        {visibleExpensesItems.map((item) => {
                          const active = isPathActive(item.to)
                          return (
                            <button
                              key={item.to}
                              onClick={() => handleNavigation(item.to)}
                              className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-md transition-colors duration-200 ${
                                active
                                  ? 'bg-neutral-100 font-semibold'
                                  : 'hover:bg-neutral-100'
                              }`}
                            >
                              <span>{item.icon}</span>
                              <span>{item.label}</span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Administration Group */}
                  {visibleAdminItems.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-neutral-500 mb-3 px-4">
                        Administration
                      </h3>
                      <div className="space-y-1">
                        {visibleAdminItems.map((item) => {
                          const active = isPathActive(item.to)
                          return (
                            <button
                              key={item.to}
                              onClick={() => handleNavigation(item.to)}
                              className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-md transition-colors duration-200 ${
                                active
                                  ? 'bg-neutral-100 font-semibold'
                                  : 'hover:bg-neutral-100'
                              }`}
                            >
                              <span>{item.icon}</span>
                              <span>{item.label}</span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Settings Menu */}
                  {visibleSettingsItems.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-neutral-500 mb-3 px-4">
                        Settings
                      </h3>
                      <div className="space-y-1">
                        {visibleSettingsItems.map((item) => (
                          <button
                            key={item.to}
                            onClick={() => handleNavigation(item.to)}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-md hover:bg-neutral-100 transition-colors duration-200"
                          >
                            {item.icon}
                            <span>{item.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Logout */}
                  <div className="border-t pt-4">
                    <button
                      onClick={async (e) => {
                        e.preventDefault()
                        await logout()
                        handleDrawerOpenChange(false)
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-md hover:bg-neutral-100 hover:cursor-pointer transition-colors duration-200"
                    >
                      <LogOut size={20} />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      </div>

      {/* Bottom spacer */}
      <div className="h-24" />
    </div>
  )
}
